import React from 'react';
import type { ClientContact } from '../../utils/clientTell';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/**
 * WHO GETS TOLD. Lifted out of TellThem.tsx on 19 Oct 2026.
 *
 * The component is unchanged — this is a move, not a rewrite. It is in its
 * own file because `TellThem.tsx` had grown to the size where a whole-file
 * write to the artifact editor is silently truncated, and a truncated write
 * to a live studio is not a risk worth taking for a layout change. Splitting
 * the half that never changes out of the half that does leaves both
 * comfortably writable.
 *
 * It renders in both states of the panel: beside the drafted email when
 * something is waiting to be announced, and on its own when nothing is. That
 * second case is the point of the split — see TellThem.tsx.
 *
 * Presentational only. Every piece of state and every write stays in
 * TellThem, so there is one place that talks to the database.
 *
 * Kept out of the client's reach by RLS, not by obscurity.
 */
export function TellWho({
  contacts,
  off,
  onToggle,
  onRemove,
  adding,
  setAdding,
  name,
  setName,
  email,
  setEmail,
  onAdd
}: {
  contacts: ClientContact[];
  off: Set<string>;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void | Promise<void>;
  adding: boolean;
  setAdding: (next: boolean) => void;
  name: string;
  setName: (next: string) => void;
  email: string;
  setEmail: (next: string) => void;
  onAdd: (event: React.FormEvent) => void;
}) {
  return <>
      <h3 className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-1630" data-mp="3935otmk9gifr">Who gets told</h3>

      <ul className="m-0 list-none p-0" data-id="element-1631" data-mp="2v3nvquzxmj0l">
        {contacts.length === 0 && <li className="border-b border-ruleDark py-6 font-display text-xl text-paper/40" data-id="element-1632" data-mp="2z5rfs80o2vxh">
            Nobody yet.
          </li>}
        {contacts.map(person => <li key={person.id} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-ruleDark py-5" data-id="element-1633" data-mp="1nr5lnu4aq9f8">
            <button type="button" onClick={() => onToggle(person.id)} aria-pressed={!off.has(person.id)} className="min-w-0 flex-1 text-left" data-id="element-1634" data-mp="3qd142iaoltab">
              <span className={['block font-display text-xl leading-none', off.has(person.id) ? 'text-paper/30 line-through' : ''].join(' ')} data-id="element-1635" data-mp="3gln09ybp67bd">
                {person.name || person.email}
              </span>
              {person.name && <span className="label-sm mt-2 block text-paper/35" data-id="element-1636" data-mp="2xols64mujr37">{person.email}</span>}
            </button>
            <button type="button" onClick={() => void onRemove(person.id)} className={BUTTON_QUIET} data-id="element-1637" data-mp="2sr3td72jms8x">
              Remove
            </button>
          </li>)}
      </ul>

      {adding ? <form onSubmit={onAdd} className="mt-7" data-id="element-1638" data-mp="qzqr6prymygk">
          <label className="block" data-id="element-1639" data-mp="qwuu13nxsdt6">
            <span className="label block text-paper/40" data-id="element-1640" data-mp="zghk5tpujgrs">Name</span>
            <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Fadi" autoFocus className={FIELD} data-id="element-1641" data-mp="13h9wivu0m1sa" />
          </label>
          <label className="mt-6 block" data-id="element-1642" data-mp="1d7cpwx51thr5">
            <span className="label block text-paper/40" data-id="element-1643" data-mp="1d97843oxqvrj">Email</span>
            <input value={email} onChange={event => setEmail(event.target.value)} placeholder="name@company.com" className={FIELD} data-id="element-1644" data-mp="gi5be92uk8wq" />
          </label>
          <div className="mt-7 flex items-center gap-6" data-id="element-1645" data-mp="pg76k7zwg7c3">
            <button type="submit" className={BUTTON_SOLID} data-id="element-1646" data-mp="ugv99qxhqcm5">
              Add
            </button>
            <button type="button" onClick={() => setAdding(false)} className={BUTTON_QUIET} data-id="element-1647" data-mp="20xgp8sb7evcc">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setAdding(true)} className={`${BUTTON_GHOST} mt-7`} data-id="element-1648" data-mp="3esp08f5vunkz">
          Add Someone
        </button>}

      <p className="label-sm mt-7 text-paper/30" data-id="element-1649" data-mp="39uxb1swmhfkh">
        Studio only. A client holding the page link cannot read this list.
      </p>
    </>;
}